import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import useAuth from './auth.context.jsx';
import useGroup from './group.context.jsx';
import userService from '../src/services/userService';
import useIsDesktop from '../src/hooks/useIsDesktop.js';
import { TOUR_VERSION, PRE_GROUP_STEPS, FULL_STEPS, resolvePageTourSteps } from '../src/tour/tourSteps.js';
import { needsLegalConsent } from '../src/utils/legalConsent.js';
import TourSheet from '../src/tour/TourSheet.jsx';
import TourSpotlight from '../src/tour/TourSpotlight.jsx';

const TourContext = createContext(null);
TourContext.displayName = 'Tour';

function phaseFor({ hasGroup, tour, localDone }) {
    const t = tour || {};
    if (hasGroup) {
        if (localDone.full || (t.fullDone && (t.version || 0) >= TOUR_VERSION)) return null;
        return 'full';
    }
    if (localDone.pre || t.preGroupDone) return null;
    return 'pre';
}

export function TourProvider({ children }) {
    const { userData, loadUserData } = useAuth();
    const { activeGroup, groupsLoaded } = useGroup();
    const isDesktop = useIsDesktop();
    const navigate = useNavigate();
    const location = useLocation();

    const [activePhase, setActivePhase] = useState(null);
    // A per-page mini tour (PAGE_TOURS in tourSteps.js) — mutually exclusive
    // with activePhase; { id, steps } while running, else null.
    const [pageTour, setPageTour] = useState(null);
    const [stepIndex, setStepIndex] = useState(0);
    const savingRef = useRef(false);
    // Survives the gap between clearing activePhase/pageTour and server/userData
    // catching up.
    const localDoneRef = useRef({ pre: false, full: false });
    const localSeenRef = useRef(new Set());
    const startingRef = useRef(false);
    // Import (and any other blocking sheet) registers here so the post-group
    // tour doesn't stack on top of it. Count, not a boolean — two sheets can
    // be open in sequence without a race that releases too early.
    const holdCountRef = useRef(0);
    const [tourHeld, setTourHeld] = useState(false);

    const holdTour = useCallback(() => {
        holdCountRef.current += 1;
        setTourHeld(true);
        return () => {
            holdCountRef.current = Math.max(0, holdCountRef.current - 1);
            setTourHeld(holdCountRef.current > 0);
        };
    }, []);

    const me = userData?.user;
    const tour = me?.tour;
    const hasGroup = !!activeGroup;
    // Mirrors legalConsentGate.jsx's own check — no tour may start while
    // that blocking sheet is up, or the two dialogs stack on screen at once.
    // ImportSplitwise uses holdTour() for the same reason: committing a
    // first group would otherwise fire the post-group tour over the "done" step.
    const legalGateOpen = needsLegalConsent(me, userData);

    // Seed local done/seen from server once per user
    useEffect(() => {
        if (!me?._id) return;
        localDoneRef.current = {
            pre: !!tour?.preGroupDone,
            full: !!(tour?.fullDone && (tour?.version || 0) >= TOUR_VERSION),
        };
        localSeenRef.current = new Set(tour?.seen || []);
    }, [me?._id]);

    // Keep local flags in sync after successful server updates
    useEffect(() => {
        if (!tour) return;
        if (tour.preGroupDone) localDoneRef.current.pre = true;
        if (tour.fullDone && (tour.version || 0) >= TOUR_VERSION) {
            localDoneRef.current.full = true;
        }
        for (const id of tour.seen || []) localSeenRef.current.add(id);
    }, [tour?.preGroupDone, tour?.fullDone, tour?.version, tour?.seen]);

    const steps = activePhase === 'pre' ? PRE_GROUP_STEPS
        : activePhase === 'full' ? FULL_STEPS
            : pageTour ? pageTour.steps
                : [];
    const step = steps[stepIndex] || null;

    const persist = useCallback(async (patch) => {
        if (savingRef.current) return;
        savingRef.current = true;
        try {
            const next = {
                version: tour?.version ?? 0,
                preGroupDone: !!tour?.preGroupDone,
                fullDone: !!tour?.fullDone,
                seen: tour?.seen || [],
                ...patch,
            };
            await userService.updateMe({ tour: next });
            await loadUserData();
        } catch {
            /* non-blocking */
        } finally {
            savingRef.current = false;
        }
    }, [tour, loadUserData]);

    const finishPhase = useCallback(async (phase) => {
        if (phase === 'pre') localDoneRef.current.pre = true;
        if (phase === 'full') {
            localDoneRef.current.pre = true;
            localDoneRef.current.full = true;
        }
        startingRef.current = false;
        setActivePhase(null);
        setStepIndex(0);
        if (phase === 'pre') {
            await persist({ preGroupDone: true, version: TOUR_VERSION });
        } else if (phase === 'full') {
            await persist({
                preGroupDone: true,
                fullDone: true,
                version: TOUR_VERSION,
            });
        }
    }, [persist]);

    const startPhase = useCallback((phase) => {
        if (startingRef.current) return;
        startingRef.current = true;
        setActivePhase(phase);
        setStepIndex(0);
        const first = phase === 'pre' ? PRE_GROUP_STEPS[0] : FULL_STEPS[0];
        if (first?.route) navigate(first.route);
        else navigate('/');
    }, [navigate]);

    // Page tours never navigate — they only start once the user is already on
    // the page they explain.
    const startPageTour = useCallback((pathname) => {
        if (startingRef.current) return;
        const resolved = resolvePageTourSteps(pathname, {
            isApartment: activeGroup?.type === 'APARTMENT',
            isDesktop,
        });
        if (!resolved || localSeenRef.current.has(resolved.id)) return;
        startingRef.current = true;
        setPageTour(resolved);
        setStepIndex(0);
    }, [activeGroup?.type, isDesktop]);

    const finishPageTour = useCallback(async () => {
        if (!pageTour) return;
        const id = pageTour.id;
        localSeenRef.current.add(id);
        startingRef.current = false;
        setPageTour(null);
        setStepIndex(0);
        await persist({ seen: [...localSeenRef.current] });
    }, [pageTour, persist]);

    useEffect(() => {
        if (!groupsLoaded || !me?._id || activePhase || startingRef.current || legalGateOpen || tourHeld) return;
        const next = phaseFor({
            hasGroup,
            tour,
            localDone: localDoneRef.current,
        });
        if (next) startPhase(next);
    }, [
        groupsLoaded,
        me?._id,
        hasGroup,
        tour?.preGroupDone,
        tour?.fullDone,
        tour?.version,
        activePhase,
        legalGateOpen,
        tourHeld,
        startPhase,
    ]);

    // One mini tour per page, the first time a signed-in user lands there —
    // never while the initial pre/full tour or the legal gate is active, and
    // never before a group exists (the empty /group shell has no anchors).
    useEffect(() => {
        if (!groupsLoaded || !me?._id || !hasGroup || activePhase || pageTour || startingRef.current || legalGateOpen || tourHeld) return;
        startPageTour(location.pathname);
    }, [
        location.pathname,
        groupsLoaded,
        me?._id,
        hasGroup,
        activePhase,
        pageTour,
        legalGateOpen,
        tourHeld,
        startPageTour,
    ]);

    useEffect(() => {
        if (!step || step.type !== 'spotlight' || !step.route) return;
        if (location.pathname !== step.route) navigate(step.route);
    }, [step, location.pathname, navigate]);

    const goNext = useCallback(() => {
        if (!activePhase && !pageTour) return;
        if (stepIndex >= steps.length - 1) {
            if (activePhase) finishPhase(activePhase);
            else finishPageTour();
            return;
        }
        setStepIndex((i) => i + 1);
    }, [activePhase, pageTour, stepIndex, steps.length, finishPhase, finishPageTour]);

    const skip = useCallback(() => {
        if (!activePhase && !pageTour) return;
        if (activePhase) finishPhase(activePhase);
        else finishPageTour();
    }, [activePhase, pageTour, finishPhase, finishPageTour]);

    const restartTour = useCallback(async () => {
        localDoneRef.current = { pre: false, full: false };
        localSeenRef.current = new Set();
        startingRef.current = false;
        setActivePhase(null);
        setPageTour(null);
        setStepIndex(0);
        await persist({
            version: 0,
            preGroupDone: false,
            fullDone: false,
            seen: [],
        });
        const phase = hasGroup ? 'full' : 'pre';
        navigate('/');
        setTimeout(() => startPhase(phase), 100);
    }, [persist, navigate, hasGroup, startPhase]);

    const value = useMemo(() => ({
        active: !!activePhase || !!pageTour,
        restartTour,
        holdTour,
    }), [activePhase, pageTour, restartTour, holdTour]);

    const readyForSpotlight = !step
        || step.type !== 'spotlight'
        || !step.route
        || location.pathname === step.route;

    return (
        <TourContext.Provider value={value}>
            {children}
            {step?.type === 'sheet' && (
                <TourSheet
                    stepId={step.id}
                    pose={step.pose}
                    stepIndex={stepIndex}
                    stepCount={steps.length}
                    onNext={goNext}
                    onSkip={skip}
                />
            )}
            {step?.type === 'spotlight' && readyForSpotlight && (
                <TourSpotlight
                    target={step.target}
                    stepId={step.id}
                    pose={step.pose}
                    stepIndex={stepIndex}
                    stepCount={steps.length}
                    onNext={goNext}
                    onSkip={skip}
                    isLast={stepIndex >= steps.length - 1}
                />
            )}
        </TourContext.Provider>
    );
}

export default function useTour() {
    return useContext(TourContext);
}
